import React, { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Flag, Sparkles, CheckCircle2, Star, X, Bot, Lock, Edit3,
  AlertCircle, ChevronDown, Clock, Database, Building2,
  ArrowRight, Loader2, Lightbulb, FileText,
} from 'lucide-react';
import { spendTier, formatCurrency } from './spendTiers';
import PoDetailModal from '../PoDetailModal/PoDetailModal';
import ClassificationChip from '../ClassificationChip';
import ClassificationAgentChat from './ClassificationAgentChat';

function fmtRelative(iso) {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '—';
  const seconds = Math.max(1, Math.round((Date.now() - t) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const m = Math.round(seconds / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}

/**
 * FlagCard — one row from the flag-review queue.
 *
 * Renders:
 *   • Header strip with tier badge, PO ref, supplier, $, age
 *   • Item description (truncated, expand-on-click)
 *   • Side-by-side: current classification ↔ flag context
 *   • Action footer with the 5 resolution paths
 *
 * Two of the five actions ("Reclassify with AI", "Manually classify") are
 * locked in Phase 1. The North Star (Principle 3) makes PoClassifier the
 * single canonical writer; the corresponding backend wrapper that routes
 * a flag-driven reclassification through PoClassifier with a wave-id is
 * Phase 2. Showing them locked surfaces the intent without leaking a
 * non-canonical write path.
 */
const HINT_MAX_LEN = 1000;

function FlagCard({ flag, onResolve, onReclassify, onApply, busy, reclassifying, applying, proposal, onClearProposal }) {
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const [dismissMenuOpen, setDismissMenuOpen] = useState(false);
  // Operator-supplied domain hint. Pre-fill with the latest hint already
  // saved on this flag (if any) so the operator can edit rather than
  // retype. Cleared after a successful reclassify; the backend persists
  // body.hint_text onto the flag and the queue refetch updates latest_hint.
  const [hint, setHint] = useState(flag.latest_hint || '');
  // PoDetailModal open state — lets operators open the full PO drill-down
  // after a classification to manually verify the AI saw the right context.
  const [poModalOpen, setPoModalOpen] = useState(false);
  const canViewPo = !!flag.purchase_document_num;
  // Classification Agent chat open state — Claude agent that can dialogue,
  // create components, add patterns, reclassify items, and resolve flags.
  const [chatOpen, setChatOpen] = useState(false);
  const tier = spendTier(flag.line_total);

  const flagsLabel = flag.flag_count > 1
    ? `${flag.flag_count} flags · ${flag.unique_flaggers} flagger${flag.unique_flaggers === 1 ? '' : 's'}`
    : '1 flag';

  const desc = flag.item_description || '';
  const truncated = desc.length > 220 && !expanded ? `${desc.slice(0, 220)}…` : desc;

  return (
    <article className={`fr-card fr-card--tier-${tier.variant}`}>
      <header className="fr-card__head">
        <div className="fr-card__head-left">
          <span className={`fr-tier fr-tier--${tier.variant}`} title={tier.description}>
            {tier.label}
          </span>
          <span className="fr-card__amount">{formatCurrency(flag.line_total)}</span>
          {flag.supplier_name && (
            <span className="fr-card__supplier" title="Supplier of record">
              <Building2 size={11} /> {flag.supplier_name}
            </span>
          )}
        </div>
        <div className="fr-card__head-right">
          <span className="fr-card__meta">
            <Flag size={11} /> {flagsLabel}
          </span>
          <span className="fr-card__meta">
            <Clock size={11} /> {fmtRelative(flag.last_flagged_at || flag.first_flagged_at)}
          </span>
          <span className="fr-card__meta fr-card__meta--mono" title="Procurement row id">
            item #{flag.item_id}
          </span>
        </div>
      </header>

      <section className="fr-card__desc">
        <p>
          {truncated}
          {desc.length > 220 && (
            <button className="fr-card__expand" onClick={() => setExpanded(e => !e)}>
              {expanded ? 'show less' : 'show more'}
            </button>
          )}
        </p>
      </section>

      <section className="fr-card__split">
        <div className="fr-card__pane">
          <h3 className="fr-card__pane-title">
            <Database size={12} /> Current classification
          </h3>
          <dl className="fr-kv">
            <dt>Classification</dt>
            <dd>
              <ClassificationChip
                itemId={flag.item_id}
                componentName={flag.current_component_name}
                confidence={flag.component_confidence}
                classificationMethod={flag.classification_model_id || flag.classification_method}
                onUpdated={() => queryClient.invalidateQueries({ queryKey: ['classification-flags'] })}
              />
            </dd>
            {flag.classification_prompt_version && (
              <>
                <dt>Prompt</dt>
                <dd className="fr-mono">{flag.classification_prompt_version}</dd>
              </>
            )}
            {flag.programs?.length > 0 && (
              <>
                <dt>Programs</dt>
                <dd>
                  <ProgramChips programs={flag.programs} />
                </dd>
              </>
            )}
          </dl>
        </div>

        <div className="fr-card__pane">
          <h3 className="fr-card__pane-title">
            <Flag size={12} /> Flag context
          </h3>
          <dl className="fr-kv">
            <dt>Categories</dt>
            <dd>
              {(flag.categories || []).length > 0 ? (
                flag.categories.map(c => (
                  <span key={c} className="fr-chip fr-chip--cat">{c.replace(/_/g, ' ')}</span>
                ))
              ) : <em className="fr-muted">—</em>}
            </dd>
            <dt>Severity</dt>
            <dd>
              {(flag.severities || []).length > 0
                ? flag.severities.map(s => <span key={s} className={`fr-chip fr-chip--sev-${s}`}>{s}</span>)
                : <em className="fr-muted">—</em>}
            </dd>
            <dt>Priority</dt>
            <dd>{flag.priority_score != null ? flag.priority_score.toFixed(2) : '—'}</dd>
          </dl>
        </div>
      </section>

      <section className="fr-card__hint" aria-label="Operator hint">
        <div className="fr-card__hint-head">
          <Lightbulb size={12} /> Domain hint for the AI
          <span className="fr-card__hint-tag">
            prepended to the LLM prompt on Reclassify-with-AI
          </span>
        </div>
        <textarea
          className="fr-card__hint-input"
          value={hint}
          onChange={(e) => setHint(e.target.value.slice(0, HINT_MAX_LEN))}
          placeholder='e.g. "BMMI = Broadband Middle-Mile Initiative, state-built fiber, not commercial IT services"'
          rows={2}
          maxLength={HINT_MAX_LEN}
          disabled={reclassifying || applying || busy}
        />
        <div className="fr-card__hint-meta">
          <span>{hint.length}/{HINT_MAX_LEN}</span>
          {flag.latest_hint && hint === flag.latest_hint && (
            <span className="fr-muted">unchanged from saved hint</span>
          )}
        </div>
      </section>

      {proposal && proposal.errorMessage && (
        <section className="fr-card__proposal fr-card__proposal--error" aria-label="Reclassify error">
          <div className="fr-card__proposal-head">
            <AlertCircle size={14} /> Reclassify failed
            {proposal.errorStatus && (
              <span className="fr-card__proposal-wave fr-mono">HTTP {proposal.errorStatus}</span>
            )}
          </div>
          <p className="fr-card__proposal-error">{proposal.errorMessage}</p>
          <div className="fr-card__proposal-actions">
            <button
              className="fr-action fr-action--primary"
              onClick={() => onReclassify(flag.item_id, { hintText: hint })}
              disabled={reclassifying || busy}
            >
              {reclassifying ? <Loader2 size={12} className="fr-spin" /> : <Sparkles size={12} />}
              Retry
            </button>
            <button
              className="fr-action fr-action--muted"
              onClick={() => onClearProposal(flag.item_id)}
              disabled={reclassifying || busy}
            >
              <X size={12} /> Dismiss error
            </button>
          </div>
        </section>
      )}

      {proposal && !proposal.errorMessage && (
        <section className="fr-card__proposal" aria-label="AI proposal">
          <div className="fr-card__proposal-head">
            <Sparkles size={14} /> Engine proposal
            <span className={`fr-trust fr-trust--${proposal.trust}`}>{proposal.trust}</span>
            {proposal.escalation?.hint_used && (
              <span className="fr-card__hint-used" title="Operator domain hint was included in the LLM prompt for this call">
                <Lightbulb size={11} /> hint used
              </span>
            )}
            {proposal.wave_id && (
              <span className="fr-card__proposal-wave fr-mono" title="Audit wave id">
                {proposal.wave_id}
              </span>
            )}
          </div>
          {proposal.proposed ? (
            <div className="fr-card__proposal-diff">
              <div className="fr-card__proposal-pane">
                <span className="fr-meta-label">Was</span>
                <strong>{proposal.previous?.component_name || <em className="fr-muted">unclassified</em>}</strong>
                <span className="fr-mono fr-muted">
                  {proposal.previous?.method || '—'}
                  {proposal.previous?.confidence != null
                    ? ` · ${(proposal.previous.confidence * 100).toFixed(0)}%`
                    : ''}
                </span>
              </div>
              <ArrowRight size={14} className="fr-card__proposal-arrow" />
              <div className="fr-card__proposal-pane">
                <span className="fr-meta-label">Proposed</span>
                <strong>{proposal.proposed.component_name || <em className="fr-muted">no match</em>}</strong>
                <span className="fr-mono fr-muted">
                  {proposal.proposed.method}
                  {proposal.proposed.confidence != null
                    ? ` · ${(proposal.proposed.confidence * 100).toFixed(0)}%`
                    : ''}
                </span>
                {proposal.programs?.length > 0 && (
                  <ProgramChips programs={proposal.programs} />
                )}
              </div>
            </div>
          ) : (
            <div className="fr-card__proposal-empty">
              <p>
                Deterministic cascade produced no proposal — dictionary, rules, and
                UNSPSC didn't match. Free methods exhausted.
              </p>
              {Number(flag.line_total) >= 10000 ? (
                <p className="fr-card__proposal-empty-action">
                  This item is{' '}
                  <strong>{tier.label} tier</strong> ($
                  {Number(flag.line_total).toLocaleString()}) — eligible for
                  Haiku escalation per the North Star spend tiers.
                </p>
              ) : (
                <p className="fr-card__proposal-empty-note">
                  Item is below the $10K Haiku floor (Tier L/T). Per the North
                  Star, sub-$10K items are allowed to remain unclassified —
                  consider <em>Confirm correct</em> or <em>Dismiss</em>.
                </p>
              )}
              {proposal.escalation?.error && (
                <p className="fr-card__proposal-error">
                  Escalation failed: {proposal.escalation.error.message}
                </p>
              )}
            </div>
          )}
          {/* Reasoning paragraph — Haiku always returns a one-liner explaining
              its proposal. The cost ledger stored it as shadow_classification_
              notes and the API surfaces it as proposed.notes. Hiding it (as we
              used to) meant operators saw only the bare component name — and
              when Haiku hallucinated an acronym expansion that drove the wrong
              classification, there was no way to spot it. */}
          {proposal.proposed?.notes && (
            <div className="fr-card__proposal-reasoning" aria-label="Engine reasoning">
              <span className="fr-meta-label">Why</span>
              <p>{proposal.proposed.notes}</p>
            </div>
          )}
          {proposal.escalation?.attempted && proposal.escalation.cost_usd > 0 && (
            <p className="fr-card__proposal-cost">
              Haiku escalation spent ${proposal.escalation.cost_usd.toFixed(4)} of
              ${proposal.escalation.cap_usd.toFixed(2)} cap
              · {proposal.escalation.items_classified} of {proposal.escalation.items_attempted} items classified
              · ${proposal.escalation.process_budget_remaining_usd.toFixed(2)} budget remaining this session
            </p>
          )}
          {/* Stage C — Sonnet agent escalation summary + tool trail.
              Tool trail is the operator's audit evidence: every glossary
              lookup, sibling inspection, similar-line search, and web
              search Sonnet performed before producing the proposal. The
              whole point is to make confident hallucinations impossible —
              you can see exactly what the agent grounded its reasoning in. */}
          {proposal.sonnet_escalation?.attempted && (
            <SonnetTrailSection escalation={proposal.sonnet_escalation} />
          )}
          {proposal.sibling_line_count > 1 && (
            <p className="fr-card__proposal-note">
              Re-ran the entire PO ({proposal.sibling_line_count} sibling lines also got fresh shadow values).
            </p>
          )}
          <div className="fr-card__proposal-actions">
            {proposal.proposed && proposal.changed && (
              <button
                className="fr-action fr-action--accent"
                onClick={() => onApply(flag.item_id)}
                disabled={applying}
              >
                {applying ? <Loader2 size={12} className="fr-spin" /> : <CheckCircle2 size={12} />}
                Apply new classification
              </button>
            )}
            {/* Escalate to Haiku — only when deterministic produced nothing
                AND the item qualifies ($10K+) AND we haven't already escalated. */}
            {!proposal.proposed
              && Number(flag.line_total) >= 10000
              && !proposal.escalation?.attempted && (
              <button
                className="fr-action fr-action--primary"
                onClick={() => onReclassify(flag.item_id, { escalate: true, hintText: hint })}
                disabled={reclassifying || applying}
                title="Re-run with Haiku escalation. Tight per-call cost cap (~$0.20) prevents runaway spend."
              >
                {reclassifying ? <Loader2 size={12} className="fr-spin" /> : <Sparkles size={12} />}
                Escalate to Haiku (~$0.02)
              </button>
            )}
            <button
              className="fr-action fr-action--muted"
              onClick={() => onClearProposal(flag.item_id)}
              disabled={applying}
            >
              <X size={12} /> Reject — keep current
            </button>
          </div>
        </section>
      )}

      <footer className="fr-card__actions">
        {/* Reclassify-with-AI: the primary action of the flag review surface.
            Escalates to Haiku for any item ≥ $10K (the spend-tier floor) so
            re-clicking actually changes something — the deterministic-only
            path was a footgun: items with an existing shadow would hit
            skip-already-specific and the operator would see the same stale
            proposal forever. Below the floor (Tier L/T, 0.5% of spend) we
            stay deterministic — Haiku isn't justified for sub-$10K items per
            CLASSIFICATION_NORTH_STAR.md.
            Hint forces escalation too: if the operator went to the trouble
            of typing a hint, they want it injected into the LLM prompt,
            which only happens with escalate=true. */}
        {(() => {
          const hasHint = hint && hint.trim().length > 0;
          const shouldEscalate = hasHint || Number(flag.line_total) >= 10000;
          const label = hasHint
            ? 'Reclassify with AI + hint'
            : shouldEscalate
              ? 'Reclassify with AI (Haiku)'
              : 'Reclassify with AI';
          const title = hasHint
            ? 'Re-run with your domain hint injected into the Haiku prompt. Result lands as a shadow proposal; you ratify with Apply.'
            : shouldEscalate
              ? `Item is $${Number(flag.line_total || 0).toLocaleString()} (Tier ${tier.key}+). Escalates to Haiku with the full PO context, glossary lookups, and cost cap ~$0.20.`
              : `Item is below the $10K Haiku floor. Runs deterministic cascade only (dict → rules → UNSPSC). Apply the shadow if it matches.`;
          return (
            <button
              className="fr-action fr-action--primary"
              onClick={() => onReclassify(flag.item_id, {
                escalate: shouldEscalate,
                hintText: hint,
              })}
              disabled={reclassifying || applying || busy}
              title={title}
            >
              {reclassifying ? <Loader2 size={12} className="fr-spin" /> : <Sparkles size={12} />}
              {label}
            </button>
          );
        })()}

        {/* Deep analyze (Sonnet) — Stage C agent path. Sonnet runs a
            tool-using loop: glossary lookups, similar past lines, PO
            siblings (ground truth — kills the "siblings classified as
            Salesforce" hallucination class), and web search for vendors
            the engine doesn't know. Cost ~$0.05-$0.25 per call,
            per-item cap $1.50, $25 process budget. Gated to items ≥$10K
            (same floor as Haiku). The dependable path when Haiku's
            answer is suspect. */}
        {Number(flag.line_total) >= 10000 && (
          <button
            className="fr-action fr-action--accent"
            onClick={() => onReclassify(flag.item_id, {
              escalate: true,
              useSonnet: true,
              hintText: hint,
            })}
            disabled={reclassifying || applying || busy}
            title={`Sonnet agent with tools: glossary lookup, sibling inspection, similar-past-line search, and live web search. Costs ~$0.05-$0.25 per call. Gives an auditable tool trail so you can verify the reasoning.`}
          >
            {reclassifying ? <Loader2 size={12} className="fr-spin" /> : <Sparkles size={12} />}
            Deep analyze (Sonnet)
          </button>
        )}

        <button
          className="fr-action fr-action--success"
          onClick={() => onResolve(flag.item_id, {
            resolution_type: 'confirmed_correct',
            status: 'resolved',
            resolution_notes: 'Operator confirms current classification is correct.',
          })}
          disabled={busy}
        >
          <CheckCircle2 size={12} /> Confirm correct
        </button>

        <button
          className="fr-action fr-action--locked"
          disabled
          title="Coming in Phase 2 — manual corrections must update dictionary/rules and re-run through PoClassifier rather than directly write classification_method (CLASSIFICATION_NORTH_STAR.md, Principles 3 + 5)."
        >
          <Lock size={12} /> <Edit3 size={12} /> Manually classify
        </button>

        <button
          className="fr-action fr-action--accent"
          onClick={() => onResolve(flag.item_id, {
            resolution_type: 'promoted_to_eval',
            status: 'resolved',
            resolution_notes: 'Operator promoted item to classification_eval_set as a gold-standard example.',
          })}
          disabled={busy}
          title="Add this row to classification_eval_set so it becomes a regression-guard prior for future engine changes."
        >
          <Star size={12} /> Promote to eval set
        </button>

        <div className="fr-action-group">
          <button
            className="fr-action fr-action--muted"
            onClick={() => setDismissMenuOpen(o => !o)}
            disabled={busy}
            aria-haspopup="menu"
            aria-expanded={dismissMenuOpen}
          >
            <X size={12} /> Dismiss <ChevronDown size={11} />
          </button>
          {dismissMenuOpen && (
            <div className="fr-action-menu" role="menu">
              <button
                className="fr-action-menu__item"
                onClick={() => {
                  setDismissMenuOpen(false);
                  onResolve(flag.item_id, {
                    resolution_type: 'dismissed_dupe',
                    status: 'dismissed',
                    resolution_notes: 'Operator dismissed: duplicate flag for an already-tracked issue.',
                  });
                }}
              >
                Duplicate flag
              </button>
              <button
                className="fr-action-menu__item"
                onClick={() => {
                  setDismissMenuOpen(false);
                  onResolve(flag.item_id, {
                    resolution_type: 'dismissed_not_actionable',
                    status: 'dismissed',
                    resolution_notes: 'Operator dismissed: acknowledged but no action possible.',
                  });
                }}
              >
                Not actionable
              </button>
            </div>
          )}
        </div>

        {/* Ask Agent — opens Classification Agent chat. Claude agent that can
            research products, create components, add patterns, reclassify items,
            and resolve flags. All actions follow CLASSIFICATION_NORTH_STAR.md. */}
        <button
          type="button"
          className="fr-action fr-action--accent"
          onClick={() => setChatOpen(true)}
          disabled={chatOpen || busy}
          title="Open Claude Agent to dialogue about this misclassification. Agent can create components, add patterns, reclassify, and resolve flags."
        >
          <Bot size={12} /> Ask Agent
        </button>

        {/* View PO — opens the standard PoDetailModal so the operator can
            manually verify the full PO context the AI saw before confirming
            or rejecting the proposal. Same modal Triage and Vendor Analysis
            use. Disabled when we don't have a purchase_document_num on the
            row. */}
        <button
          type="button"
          className="fr-action fr-action--muted"
          onClick={() => setPoModalOpen(true)}
          disabled={!canViewPo}
          title={canViewPo
            ? `Open PO ${flag.purchase_document_num} in the standard document modal — see every sibling line + supplier + totals to verify the engine's context.`
            : 'No purchase document number on this row — cannot open PO view'}
        >
          <FileText size={12} /> View PO
        </button>

        {busy && (
          <span className="fr-action__busy">
            <AlertCircle size={11} /> Resolving…
          </span>
        )}
      </footer>

      {poModalOpen && canViewPo && (
        <PoDetailModal
          documentNumber={flag.purchase_document_num}
          departmentId={flag.department_id}
          startDate={flag.start_date}
          isOpen={poModalOpen}
          onClose={() => setPoModalOpen(false)}
        />
      )}

      {chatOpen && (
        <ClassificationAgentChat
          flag={flag}
          onClose={() => setChatOpen(false)}
          onResolved={() => {
            setChatOpen(false);
            // Trigger a refresh of the flag queue
            queryClient.invalidateQueries({ queryKey: ['classification-flags'] });
          }}
        />
      )}
    </article>
  );
}

/**
 * ProgramChips — render the second classification dimension (CA state
 * programs the line supports). Component is the WHAT (IT Consulting);
 * programs are the WHICH-initiative (CAPMAN, BMMI, etc.). One row can
 * have multiple chips when a contract touches several systems.
 *
 * Source = 'sonnet-agent' is the highest-trust origin (an agent ran
 * the link_program tool with grounded evidence). 'haiku' is the
 * auto-linker firing when a glossary hit landed in the prompt. Both
 * render identically here — the tooltip surfaces the difference.
 */
function ProgramChips({ programs }) {
  if (!programs?.length) return null;
  return (
    <div className="fr-program-chips">
      {programs.map(p => (
        <span
          key={p.program_id}
          className={`fr-chip fr-program-chip fr-program-chip--${p.source || 'unknown'}`}
          title={`${p.name}${p.agency_acronym ? ` · ${p.agency_acronym}` : ''} (linked via ${p.source}, conf ${p.confidence != null ? (p.confidence * 100).toFixed(0) + '%' : '—'})`}
        >
          {p.acronym}
        </span>
      ))}
    </div>
  );
}

/**
 * Sonnet escalation block — collapsible summary + tool trail.
 *
 * The whole point of Stage C: turn confident hallucinations into
 * auditable reasoning. The operator can expand the trail and see every
 * tool call Sonnet made (glossary lookups, sibling inspection, similar
 * past-line searches, web searches), each with the input and the
 * truncated result. If a classification looks wrong, the trail tells
 * you exactly which evidence drove it.
 */
function SonnetTrailSection({ escalation }) {
  const [open, setOpen] = useState(false);
  const trail = escalation.tool_trail || [];
  const trailCount = trail.length;
  const cost = Number(escalation.cost_usd || 0);

  return (
    <div className="fr-card__sonnet">
      <div className="fr-card__sonnet-head">
        <Sparkles size={12} />
        <strong>Sonnet agent</strong>
        <span className="fr-mono fr-muted">
          ${cost.toFixed(4)} of ${Number(escalation.cap_usd || 0).toFixed(2)} cap
          · {escalation.tool_calls || 0} tool calls
          · {escalation.web_searches || 0} web searches
        </span>
        {escalation.error && (
          <span className="fr-card__proposal-error">
            error: {escalation.error.message}
          </span>
        )}
        {trailCount > 0 && (
          <button
            type="button"
            className="fr-action fr-action--ghost fr-card__sonnet-toggle"
            onClick={() => setOpen(o => !o)}
          >
            <ChevronDown
              size={12}
              style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}
            />
            {open ? 'Hide' : 'Show'} tool trail ({trailCount})
          </button>
        )}
      </div>
      {open && trailCount > 0 && (
        <ol className="fr-card__sonnet-trail">
          {trail.map((t, i) => (
            <li key={i} className="fr-card__sonnet-step">
              <div className="fr-card__sonnet-step-head">
                <span className="fr-mono">{t.tool}</span>
                <span className="fr-muted fr-card__sonnet-step-input">
                  {JSON.stringify(t.input).slice(0, 140)}
                </span>
              </div>
              <pre className="fr-card__sonnet-step-result">
                {truncateForTrail(JSON.stringify(t.result, null, 2))}
              </pre>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function truncateForTrail(s) {
  if (!s) return '';
  const MAX = 600;
  return s.length > MAX ? s.slice(0, MAX) + '…' : s;
}

export default FlagCard;
